import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Header from '@/components/Header';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Clock, Users, Video, Award, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const SchedulePage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      setClasses(response.data);
    } catch (error) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (classId) => {
    if (!user) {
      toast.error('Please login to enroll');
      navigate('/auth');
      return;
    }

    if (user.role !== 'student') {
      toast.error('Only students can enroll in classes');
      return;
    }

    try {
      await axios.post(`${API}/enrollments?class_id=${classId}`);
      toast.success('Successfully enrolled!');
      navigate('/dashboard/student');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Enrollment failed');
    }
  };

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const groupClassesByDay = () => {
    const grouped = {};
    daysOfWeek.forEach(day => {
      grouped[day] = classes.filter(cls => cls.schedule.toLowerCase().includes(day.substring(0, 3).toLowerCase()));
    });
    return grouped;
  };

  const scheduleByDay = groupClassesByDay();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="schedule-page">
      <SEO
        title="Schedule"
        description="View class schedules at Sun Kamal Academy. Live Zoom sessions for Chess, Tabla, Yoga and more."
        path="/schedule"
      />
      <Header />
      
      {/* Page Header */}
      <div className="pt-24 sm:pt-28 pb-8 sm:pb-12 bg-gradient-to-r from-blue-500 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-2 sm:mb-4" data-testid="page-title">
              <Video className="w-8 h-8 sm:w-10 md:w-12 sm:h-10 md:h-12 inline-block mr-2 sm:mr-4" />
              Online Class Schedule
            </h1>
            <p className="text-base sm:text-lg md:text-xl opacity-90">Live Zoom sessions with expert instructors</p>
            <div className="mt-4 sm:mt-6 flex gap-2 sm:gap-4 justify-center flex-wrap">
              <div className="bg-white/10 backdrop-blur px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm md:text-base">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 inline-block mr-1 sm:mr-2" />
                8-Week Courses
              </div>
              <div className="bg-white/10 backdrop-blur px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm md:text-base">
                <Award className="w-4 h-4 sm:w-5 sm:h-5 inline-block mr-1 sm:mr-2" />
                Certificate Provided
              </div>
              <div className="bg-white/10 backdrop-blur px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm md:text-base">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 inline-block mr-1 sm:mr-2" />
                Discounts Available
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8 md:py-12">
        {loading ? (
          <div className="text-center py-16 sm:py-20">Loading schedule...</div>
        ) : (
          <>
            {/* All Classes Overview */}
            <div className="mb-10 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Available Courses</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {classes.map((cls, index) => (
                  <Card key={cls.id} className="hover:shadow-xl transition" data-testid={`class-card-${index}`}>
                    <img src={cls.image} alt={cls.title} className="w-full h-40 sm:h-48 object-cover rounded-t-lg" loading="lazy" />
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex items-center gap-2 mb-2 sm:mb-3 flex-wrap">
                        <span className="px-2 sm:px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-xs sm:text-sm font-medium">
                          {cls.sport}
                        </span>
                        <span className="px-2 sm:px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs sm:text-sm font-medium">
                          {cls.course_duration}
                        </span>
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold mb-2">{cls.title}</h3>
                      <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                          <Calendar className="w-4 h-4 shrink-0" />
                          <span className="truncate">{cls.schedule}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                          <Clock className="w-4 h-4 shrink-0" />
                          <span>{cls.duration} minutes per session</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                          <Users className="w-4 h-4 shrink-0" />
                          <span>{cls.enrolled_count}/{cls.max_students} enrolled</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                          <Award className="w-4 h-4 shrink-0" />
                          <span>By {cls.teacher_name}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <div className="text-2xl sm:text-3xl font-bold text-orange-600">${cls.price}</div>
                        <div className="text-xs sm:text-sm text-green-600 font-medium">10% off available</div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => navigate(`/classes/${cls.id}`)} variant="outline" className="flex-1 text-xs sm:text-sm">
                          View Details
                        </Button>
                        <Button 
                          onClick={() => handleEnroll(cls.id)} 
                          className="btn-primary flex-1 text-xs sm:text-sm"
                          disabled={cls.enrolled_count >= cls.max_students}
                          data-testid={`enroll-btn-${index}`}
                        >
                          {cls.enrolled_count >= cls.max_students ? 'Full' : 'Enroll Now'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Weekly Schedule */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Weekly Schedule</h2>
              <div className="space-y-4 sm:space-y-6">
                {daysOfWeek.map(day => (
                  <Card key={day}>
                    <CardHeader className="pb-2 sm:pb-4">
                      <CardTitle className="flex items-center gap-2 sm:gap-3 text-base sm:text-lg">
                        <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500" />
                        {day}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {scheduleByDay[day].length === 0 ? (
                        <p className="text-gray-500 text-sm">No classes scheduled</p>
                      ) : (
                        <div className="space-y-3 sm:space-y-4">
                          {scheduleByDay[day].map((cls) => (
                            <div key={cls.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition gap-3">
                              <div className="flex-1">
                                <h4 className="font-bold text-base sm:text-lg">{cls.title}</h4>
                                <div className="flex items-center gap-3 sm:gap-4 mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600 flex-wrap">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {cls.schedule}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Award className="w-4 h-4" />
                                    {cls.teacher_name}
                                  </span>
                                  <span className="font-semibold text-orange-600">${cls.price}</span>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button 
                                  onClick={() => navigate(`/classes/${cls.id}`)} 
                                  variant="outline"
                                  size="sm"
                                  className="text-xs sm:text-sm"
                                >
                                  Details
                                </Button>
                                <Button 
                                  onClick={() => handleEnroll(cls.id)}
                                  className="btn-primary text-xs sm:text-sm"
                                  size="sm"
                                  disabled={cls.enrolled_count >= cls.max_students}
                                >
                                  <Video className="w-4 h-4 mr-1" />
                                  Enroll
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Payment Options */}
            <Card className="mt-8 sm:mt-12 bg-gradient-to-r from-orange-50 to-yellow-50">
              <CardContent className="p-4 sm:p-6 md:p-8">
                <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-center">Flexible Payment Options</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                  <div className="text-center">
                    <div className="text-3xl sm:text-4xl mb-2 sm:mb-3" aria-hidden="true">💰</div>
                    <h3 className="font-bold mb-1 sm:mb-2 text-sm sm:text-base">Installment Plan</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Pay 50% upfront, 50% midway through course</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl sm:text-4xl mb-2 sm:mb-3" aria-hidden="true">🎯</div>
                    <h3 className="font-bold mb-1 sm:mb-2 text-sm sm:text-base">Early Bird Discount</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Get 10% off when you enroll early</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl sm:text-4xl mb-2 sm:mb-3" aria-hidden="true">👨‍👩‍👧‍👦</div>
                    <h3 className="font-bold mb-1 sm:mb-2 text-sm sm:text-base">Sibling Discount</h3>
                    <p className="text-xs sm:text-sm text-gray-600">10% discount for multiple siblings</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-6 sm:py-8 mt-8 sm:mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-400 text-sm">Need help? Email us at <a href="mailto:info@sunkamalsports.online" className="text-orange-400 hover:text-orange-300">info@sunkamalsports.online</a></p>
        </div>
      </footer>
    </div>
  );
};

export default SchedulePage;
